import { compose, css } from '@wrisk/styled-system'
import {
  PrimaryButton,
  transition,
  TransitionProps,
  Typo,
  WideContainer,
  withDefaultProps,
} from '@wrisk/ui-components'
import { TFunction } from 'i18next'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { TKeyBuilder } from '../../../infrastructure/internationalisation'
import { usePortal } from '../../../util/portal'
import { INPUT_SECTION_CLASS } from '../options'

const defaultProps = withDefaultProps<TransitionProps>({
  transition: 'default',
})

const ErrorContainer = styled.div.attrs<TransitionProps>(defaultProps)(
  css({
    position: 'fixed',
    width: '100%',
    bottom: 0,
    left: 0,
    py: 6,
    bg: 'chalk',
    boxShadow: 'default',
    zIndex: 200,
  }),
  compose(transition),
)

export interface FormErrorsProps {
  errorClass: string
  tKey: TKeyBuilder
  t: TFunction
}

export const FormErrorsPortal: FunctionComponent<FormErrorsProps> = ({
  t,
  tKey,
  errorClass,
}) => {
  const portal = usePortal()
  const { formState } = useFormContext()

  const values = useMemo(() => Object.values(formState.errors), [formState])

  const onShowMe = useCallback(() => {
    const [firstError] = document.getElementsByClassName(errorClass)
    firstError?.closest(`.${INPUT_SECTION_CLASS}`)?.scrollIntoView({ behavior: 'smooth' })
  }, [errorClass])

  return createPortal(
    values.length > 0 ? (
      <ErrorContainer>
        <WideContainer
          flexDirection='row'
          justifyContent='space-between'
          flexWrap='wrap'
          alignItems='center'
        >
          <Typo fontWeight='bold' pr={4} pb={[4, 4, 0]}>
            {t([tKey('validation.summary'), 'defaults.validation.summary'], {
              count: values.length,
            })}
          </Typo>
          <PrimaryButton mr={13} whiteSpace='nowrap' onClick={onShowMe}>
            {t([tKey('validation.actions.show-me'), 'defaults.actions.show-me'])}
          </PrimaryButton>
        </WideContainer>
      </ErrorContainer>
    ) : null,
    portal,
  )
}
